import { useState } from "react";
import { Link } from "react-router-dom";
import { Package, Truck, CheckCircle, Clock, AlertCircle } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { supabase } from "../lib/supabase";

const STATUS_LABELS = {
	processing: "In lavorazione",
	shipped: "Spedito",
	delivered: "Consegnato",
	delayed: "Annullato",
} as const;

export default function OrderTracking() {
	const [orderNumber, setOrderNumber] = useState("");
	const [email, setEmail] = useState("");
	const [isTracking, setIsTracking] = useState(false);
	const [trackingError, setTrackingError] = useState<string | null>(null);
	const [trackingResult, setTrackingResult] = useState<null | {
		orderNumber: string;
		status: "processing" | "shipped" | "delivered" | "delayed";
		date: string;
		items: number;
		estimatedDelivery?: string;
		trackingNumber?: string;
		carrier?: string;
		steps: {
			title: string;
			description: string;
			date: string;
			completed: boolean;
		}[];
	}>(null);

	const { user } = useAuthStore();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setTrackingResult(null);
		setTrackingError(null);

		if (!orderNumber.trim() || !email.trim()) {
			setTrackingError("Inserisci numero ordine e email per continuare.");
			return;
		}

		setIsTracking(true);

		try {
			// Le email mostrano il codice come "#ABCD1234": il "#" va tolto.
			const { data, error } = await supabase.rpc("get_order_tracking", {
				p_order_code: orderNumber.trim().replace(/^#/, ""),
				p_email: email,
			});

			if (error) {
				throw error;
			}

			const order = data?.[0];

			if (!order) {
				setTrackingError("Nessun ordine trovato con i dati inseriti.");
				return;
			}

			const mapStatus = (
				status: string,
			): "processing" | "shipped" | "delivered" | "delayed" => {
				if (status === "shipped") return "shipped";
				if (status === "delivered" || status === "completed")
					return "delivered";
				if (status === "cancelled" || status === "refunded") return "delayed";
				return "processing";
			};

			const currentStatus = mapStatus(order.status);
			const createdDate = new Date(order.created_at);

			setTrackingResult({
				orderNumber: order.order_id.slice(-8).toUpperCase(),
				status: currentStatus,
				date: createdDate.toLocaleDateString("it-IT"),
				items: 1,
				estimatedDelivery: undefined,
				trackingNumber: order.tracking_number || "In preparazione",
				carrier: "Corriere nazionale",
				steps: [
					{
						title: "Ordine ricevuto",
						description: "Il tuo ordine è stato ricevuto.",
						date: createdDate.toLocaleString("it-IT"),
						completed: true,
					},
					{
						title: "Pagamento",
						description:
							order.payment_status === "paid"
								? "Pagamento confermato."
								: "Pagamento in verifica.",
						date: createdDate.toLocaleString("it-IT"),
						completed: order.payment_status === "paid",
					},
					{
						title: "In preparazione",
						description: "Stiamo preparando il tuo ordine.",
						date: "",
						completed: [
							"processing",
							"shipped",
							"delivered",
							"completed",
						].includes(order.status),
					},
					{
						title: "Spedito",
						description: order.tracking_number
							? `Ordine spedito. Tracking: ${order.tracking_number}`
							: "L'ordine non è ancora stato spedito.",
						date: "",
						completed: ["shipped", "delivered", "completed"].includes(
							order.status,
						),
					},
					{
						title: "Consegnato",
						description: "Consegna completata.",
						date: "",
						completed: ["delivered", "completed"].includes(order.status),
					},
				],
			});
		} catch (err) {
			console.error("Tracking lookup failed", err);
			setTrackingResult(null);
			setTrackingError(
				"Errore durante la ricerca ordine. Riprova tra qualche istante.",
			);
		} finally {
			setIsTracking(false);
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "processing":
				return <Clock className="h-6 w-6 text-blue-500" />;
			case "shipped":
				return <Truck className="h-6 w-6 text-primary" />;
			case "delivered":
				return <CheckCircle className="h-6 w-6 text-green-500" />;
			case "delayed":
				return <AlertCircle className="h-6 w-6 text-yellow-500" />;
			default:
				return <Package className="h-6 w-6 text-muted-foreground" />;
		}
	};

	return (
		<section className="container py-10">
			<h1 className="text-3xl font-bold text-foreground mb-6">
				Traccia il tuo ordine
			</h1>

			<div className="bg-card rounded-lg shadow-md p-6 mb-8">
				<h2 className="text-xl font-semibold text-foreground mb-4">
					Cerca il tuo ordine
				</h2>

				<form onSubmit={handleSubmit} className="space-y-4">
					<div>
						<label
							htmlFor="orderNumber"
							className="block text-sm font-medium text-foreground/80 mb-1"
						>
							Numero ordine
						</label>
						<input
							type="text"
							id="orderNumber"
							value={orderNumber}
							onChange={(e) => setOrderNumber(e.target.value)}
							placeholder="es. #1A2B3C4D (lo trovi nell'email di conferma)"
							className="w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
						/>
					</div>

					<div>
						<label
							htmlFor="email"
							className="block text-sm font-medium text-foreground/80 mb-1"
						>
							Email
						</label>
						<input
							type="email"
							id="email"
							value={email || user?.email || ""}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="L'email usata per l'ordine"
							className="w-full px-4 py-2 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
						/>
					</div>

					<button
						type="submit"
						disabled={isTracking}
						className="w-full md:w-auto px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed btn-hover-scale"
					>
						{isTracking ? "Ricerca in corso..." : "Cerca ordine"}
					</button>

					{trackingError && (
						<p className="text-sm text-red-600 dark:text-red-400">
							{trackingError}
						</p>
					)}
				</form>
			</div>

			{trackingResult && (
				<div className="bg-card rounded-lg shadow-md p-6">
					<div className="flex items-center justify-between mb-6">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Ordine #{trackingResult.orderNumber}
							</h2>
							<p className="text-muted-foreground text-sm">
								Effettuato il {trackingResult.date}
							</p>
						</div>
						<div className="flex items-center">
							{getStatusIcon(trackingResult.status)}
							<span className="ml-2 font-medium text-foreground">
								{STATUS_LABELS[trackingResult.status]}
							</span>
						</div>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
						<div className="bg-muted p-4 rounded-lg">
							<h3 className="text-lg font-medium text-foreground mb-2">
								Spedizione
							</h3>
							<p className="text-muted-foreground">
								<span className="font-medium">Corriere:</span>{" "}
								{trackingResult.carrier}
							</p>
							<p className="text-muted-foreground">
								<span className="font-medium">Numero di tracking:</span>{" "}
								{trackingResult.trackingNumber}
							</p>
							<p className="text-muted-foreground">
								<span className="font-medium">Consegna prevista:</span>{" "}
								{trackingResult.estimatedDelivery || "Da definire"}
							</p>
						</div>

						<div className="bg-muted p-4 rounded-lg">
							<h3 className="text-lg font-medium text-foreground mb-2">
								Serve aiuto?
							</h3>
							<p className="text-muted-foreground mb-3">
								Se hai domande sul tuo ordine, scrivici: ti rispondiamo al più presto.
							</p>
							<Link
								to="/contact"
								className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors btn-hover-scale"
							>
								Contattaci
							</Link>
						</div>
					</div>

					<h3 className="text-lg font-medium text-foreground mb-4">
						Stato dell'ordine
					</h3>

					<div className="relative">
						{trackingResult.steps.map((step, index) => (
							<div key={index} className="flex mb-6 last:mb-0">
								<div className="flex flex-col items-center mr-4">
									<div
										className={`rounded-full h-8 w-8 flex items-center justify-center ${
											step.completed
												? "bg-secondary text-foreground"
												: "bg-muted text-muted-foreground"
										}`}
									>
										{step.completed ? (
											<CheckCircle className="h-5 w-5" />
										) : (
											<Clock className="h-5 w-5" />
										)}
									</div>
									{index < trackingResult.steps.length - 1 && (
										<div
											className={`w-0.5 h-full ${
												step.completed
													? "bg-secondary"
													: "bg-muted"
											}`}
										></div>
									)}
								</div>
								<div>
									<h4 className="text-md font-medium text-foreground">
										{step.title}
									</h4>
									<p className="text-muted-foreground text-sm">
										{step.description}
									</p>
									<p className="text-muted-foreground text-xs mt-1">
										{step.date}
									</p>
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</section>
	);
}
